import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatTime, STATUS_LABELS, STATUS_CLASSES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChartBar, CheckCircle, Clock, CurrencyCircleDollar, Student, WarningCircle, CaretRight, Eye, EyeSlash
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import LessonDialog from "@/components/LessonDialog";
import ConfirmLessonDialog from "@/components/ConfirmLessonDialog";

const HIDE_KEY = "tutorpro:hideMoney";

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [students, setStudents] = useState([]);
  const [confirmLesson, setConfirmLesson] = useState(null);
  const [hideMoney, setHideMoney] = useState(() => {
    try { return localStorage.getItem(HIDE_KEY) === "1"; } catch { return false; }
  });

  function toggleHide() {
    setHideMoney((prev) => {
      const next = !prev;
      try { localStorage.setItem(HIDE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  }

  async function load() {
    const [d, m, s] = await Promise.all([
      api.get("/stats/dashboard"),
      api.get("/stats/monthly", { params: { months: 6 } }),
      api.get("/students"),
    ]);
    setStats(d.data);
    setMonthly(m.data);
    setStudents(s.data);
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const h = () => setOpenNew(true);
    window.addEventListener("tutorpro:newLesson", h);
    return () => window.removeEventListener("tutorpro:newLesson", h);
  }, []);

  function openConfirm(lesson) {
    setConfirmLesson(lesson);
  }

  async function setStatus(id, status) {
    try {
      await api.patch(`/lessons/${id}`, { status });
      toast.success("Ders güncellendi.");
      load();
    } catch { toast.error("Güncelleme başarısız."); }
  }

  const c = user?.currency || "TRY";

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-sm text-muted-foreground">Hoş geldin 👋</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{user?.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleHide}
            className="gap-2"
            title={hideMoney ? "Tutarları göster" : "Tutarları gizle"}
            data-testid="toggle-hide-money"
          >
            {hideMoney ? <EyeSlash size={16} weight="duotone" /> : <Eye size={16} weight="duotone" />}
            <span className="hidden sm:inline">{hideMoney ? "Tutarları göster" : "Tutarları gizle"}</span>
          </Button>
          <Button onClick={() => setOpenNew(true)} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="dashboard-new-lesson-button">
            + Yeni Ders Ekle
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Bu ay kazanç"
          value={formatCurrency(stats?.month_earned, c)}
          icon={CurrencyCircleDollar}
          tone="primary"
          testid="stat-earned"
          blur={hideMoney}
        />
        <StatCard
          label="Alınan ödeme"
          value={formatCurrency(stats?.month_paid, c)}
          icon={CheckCircle}
          tone="success"
          testid="stat-paid"
          blur={hideMoney}
        />
        <StatCard
          label="Alacak bakiye"
          value={formatCurrency(stats?.month_outstanding, c)}
          icon={WarningCircle}
          tone="accent"
          testid="stat-outstanding"
          blur={hideMoney}
        />
        <StatCard
          label="Tamamlanan ders"
          value={`${stats?.month_lessons_completed ?? 0}`}
          icon={ChartBar}
          tone="neutral"
          testid="stat-lessons"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Today lessons */}
        <Card className="lg:col-span-2 border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display">Bugünkü Dersler</CardTitle>
            <Link to="/takvim" className="text-sm text-[#3A5A40] hover:underline flex items-center gap-1">
              Takvim <CaretRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {(!stats || stats.today_lessons.length === 0) ? (
              <EmptyState icon={Clock} title="Bugün planlı ders yok" desc="Takvim sayfasından yeni ders ekleyebilirsiniz." />
            ) : (
              <ul className="divide-y divide-border">
                {stats.today_lessons.map((l) => (
                  <li key={l.id} className="flex items-center gap-3 py-3" data-testid={`today-lesson-${l.id}`}>
                    <div className="w-1 h-10 rounded-full" style={{ background: l.student_color || "#3A5A40" }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{l.student_name}</span>
                        <StatusBadge status={l.status} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {formatTime(l.start_time)}–{formatTime(l.end_time)} · {l.subject || "Ders"}
                      </div>
                    </div>
                    {l.status === "scheduled" && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-8" onClick={() => openConfirm(l)} data-testid={`confirm-lesson-${l.id}`}>
                          <CheckCircle size={14} weight="duotone" className="mr-1" /> Onayla
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Pending confirmations */}
        <Card className="border-border">
          <CardHeader><CardTitle className="font-display">Onay Bekleyenler</CardTitle></CardHeader>
          <CardContent>
            {(!stats || stats.pending_confirmations.length === 0) ? (
              <EmptyState icon={CheckCircle} title="Bekleyen onay yok" desc="Bitmiş dersleri buradan hızlıca işaretleyin." />
            ) : (
              <ul className="space-y-3">
                {stats.pending_confirmations.slice(0, 6).map((l) => (
                  <li key={l.id} className="border border-border rounded-md p-3" data-testid={`pending-${l.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate text-sm">{l.student_name}</div>
                        <div className="text-xs text-muted-foreground">{formatTime(l.start_time)} · {l.subject || "Ders"}</div>
                      </div>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      <Button size="sm" className="h-7 bg-[#3A5A40] hover:bg-[#2E4833] text-white" onClick={() => openConfirm(l)} data-testid={`pending-confirm-${l.id}`}>Tamamlandı</Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setStatus(l.id, "cancelled_paid")}>İptal (Ücretli)</Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setStatus(l.id, "cancelled_free")}>İptal (Ücretsiz)</Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setStatus(l.id, "postponed")}>Ertelendi</Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border">
          <CardHeader><CardTitle className="font-display">Aylık Gelir ve Gider</CardTitle></CardHeader>
          <CardContent>
            <div
              className={hideMoney ? "select-none pointer-events-none" : ""}
              style={{ filter: hideMoney ? "blur(10px)" : "none", transition: "filter 300ms ease", height: "16rem" }}
              aria-hidden={hideMoney}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} barGap={8}>
                  <CartesianGrid stroke="#E5E4E0" vertical={false} />
                  <XAxis dataKey="month" stroke="#5C6A5D" fontSize={12} />
                  <YAxis stroke="#5C6A5D" fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E5E4E0", borderRadius: 8 }}
                    formatter={(v) => formatCurrency(v, c)}
                  />
                  <Bar dataKey="income" fill="#3A5A40" radius={[6, 6, 0, 0]} name="Gelir" />
                  <Bar dataKey="expenses" fill="#D97757" radius={[6, 6, 0, 0]} name="Gider" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="font-display">Alacaklar</CardTitle></CardHeader>
          <CardContent>
            {(!stats || stats.outstanding_by_student.length === 0) ? (
              <EmptyState icon={Student} title="Açık bakiye yok" desc="Tüm ödemeler güncel." />
            ) : (
              <ul className="space-y-2">
                {stats.outstanding_by_student.map((s) => (
                  <li key={s.id} className="flex items-center justify-between border border-border rounded-md p-3" data-testid={`outstanding-${s.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                      <span className="truncate font-medium text-sm">{s.name}</span>
                    </div>
                    <span
                      className={`font-mono text-sm text-[#D97757] font-semibold ${hideMoney ? "select-none" : ""}`}
                      style={{ filter: hideMoney ? "blur(6px)" : "none", transition: "filter 300ms ease" }}
                    >{formatCurrency(s.balance, c)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <LessonDialog open={openNew} onOpenChange={setOpenNew} students={students} onSaved={load} />
      <ConfirmLessonDialog
        lesson={confirmLesson}
        open={!!confirmLesson}
        onOpenChange={(v) => !v && setConfirmLesson(null)}
        onConfirmed={load}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "primary", testid, blur = false }) {
  const tones = {
    primary: "bg-[#3A5A40] text-white",
    success: "bg-[#E8F5E9] text-[#2E7D32]",
    accent: "bg-[#FFF3E0] text-[#E65100]",
    neutral: "bg-muted text-foreground",
  };
  return (
    <div className="bg-white border border-border rounded-lg p-5 transition-all duration-200 hover:shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
          <div
            className={`font-display text-2xl md:text-3xl font-bold mt-2 ${blur ? "select-none pointer-events-none" : ""}`}
            style={{ filter: blur ? "blur(10px)" : "none", transition: "filter 300ms ease" }}
            aria-hidden={blur}
          >
            {value}
          </div>
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone]}`}>
          <Icon size={20} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${STATUS_CLASSES[status] || ""}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function EmptyState({ icon: Icon, title, desc }) {
  return (
    <div className="text-center py-8">
      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <Icon size={22} weight="duotone" className="text-muted-foreground" />
      </div>
      <div className="font-medium text-sm">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{desc}</div>
    </div>
  );
}
